/**
 * ISPOperators — /wan/operators
 * List, create and manage ISP operators.
 * Expanding an operator shows its circuits (with inline demarc points).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_BADGE = {
  active:   'text-bg-success',
  inactive: 'text-bg-secondary',
};

const STATUS_LABEL = {
  active:   'Activo',
  inactive: 'Inactivo',
};

const EMPTY_FORM = {
  name: '', country: '', asn: '', tech_contact: '', commercial_contact: '',
  sla: '', contract_ref: '', status: 'active', notes: '',
};

export default function ISPOperators({ companyId }) {
  const navigate = useNavigate();

  const [operators, setOperators]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const errTimer                    = useRef(null);

  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [formOpen, setFormOpen]     = useState(false);
  const [editId,   setEditId]       = useState(null);
  const [form,     setForm]         = useState(EMPTY_FORM);
  const [saving,   setSaving]       = useState(false);

  const [expandedId, setExpandedId]       = useState(null);
  const [expandData, setExpandData]       = useState({});   // { opId: {circuits:[...]} }
  const [loadingExpand, setLoadingExpand] = useState(false);

  /* Auto-dismiss error */
  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 6000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.getIspOperators(companyId, {
        ...(filterStatus && { status: filterStatus }),
        ...(search && { q: search }),
      });
      setOperators(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  /* Expand / collapse operator row to show its circuits */
  const toggleExpand = useCallback(async (opId) => {
    if (expandedId === opId) { setExpandedId(null); return; }
    setExpandedId(opId);
    if (expandData[opId]) return;
    setLoadingExpand(true);
    try {
      const res = await api.getIspOperator(opId, companyId);
      setExpandData(prev => ({ ...prev, [opId]: res.data }));
    } catch { /* ignore */ }
    finally { setLoadingExpand(false); }
  }, [expandedId, expandData, companyId]);

  /* Open form for create or edit */
  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };
  const openEdit = (op) => {
    setEditId(op.id);
    setForm({
      name: op.name || '', country: op.country || '', asn: op.asn ?? '',
      tech_contact: op.tech_contact || '', commercial_contact: op.commercial_contact || '',
      sla: op.sla || '', contract_ref: op.contract_ref || '',
      status: op.status || 'active', notes: op.notes || '',
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, asn: form.asn ? Number(form.asn) : null };
      if (editId) {
        await api.updateIspOperator(editId, payload, companyId);
        /* refresh expand cache if open */
        setExpandData(prev => { const n = { ...prev }; delete n[editId]; return n; });
      } else {
        await api.createIspOperator(payload, companyId);
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (op) => {
    if (!window.confirm(`¿Eliminar operador "${op.name}" y todos sus circuitos?`)) return;
    try {
      await api.deleteIspOperator(op.id, companyId);
      await load();
    } catch (e) { setError(e.message); }
  };

  const filtered = operators.filter(op =>
    !search || op.name.toLowerCase().includes(search.toLowerCase()) ||
    (op.country || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="container-fluid py-3">
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* Toolbar */}
      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <h5 className="mb-0 me-2">📡 Operadores ISP</h5>
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 240 }}
          placeholder="Buscar nombre o país…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 130 }}
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">Todos</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
        </select>
        <span className="badge text-bg-secondary ms-auto">
          {filtered.length} operador{filtered.length !== 1 ? 'es' : ''}
        </span>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Nuevo operador</button>
        <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>↺</button>
        <button
          className="btn btn-outline-info btn-sm"
          onClick={() => navigate('/wan/circuits')}
        >
          Ver circuitos →
        </button>
      </div>

      {/* Create / Edit form */}
      {formOpen && (
        <div className="card mb-3 border-primary shadow-sm">
          <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between py-2">
            <strong>{editId ? 'Editar operador' : 'Nuevo operador ISP'}</strong>
            <button className="btn-close" onClick={() => setFormOpen(false)} />
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit} className="row g-2">
              <div className="col-md-4">
                <label className="form-label form-label-sm mb-1">Nombre *</label>
                <input className="form-control form-control-sm" required value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm mb-1">País</label>
                <input className="form-control form-control-sm" placeholder="ES" value={form.country}
                  onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm mb-1">ASN</label>
                <input className="form-control form-control-sm" type="number" placeholder="12345" value={form.asn}
                  onChange={e => setForm(f => ({ ...f, asn: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label form-label-sm mb-1">Estado</label>
                <select className="form-select form-select-sm" value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label form-label-sm mb-1">Contacto técnico</label>
                <input className="form-control form-control-sm" placeholder="noc@isp.com" value={form.tech_contact}
                  onChange={e => setForm(f => ({ ...f, tech_contact: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label form-label-sm mb-1">Contacto comercial</label>
                <input className="form-control form-control-sm" value={form.commercial_contact}
                  onChange={e => setForm(f => ({ ...f, commercial_contact: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">SLA</label>
                <input className="form-control form-control-sm" placeholder="99.9%" value={form.sla}
                  onChange={e => setForm(f => ({ ...f, sla: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label form-label-sm mb-1">Ref. contrato</label>
                <input className="form-control form-control-sm" value={form.contract_ref}
                  onChange={e => setForm(f => ({ ...f, contract_ref: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label form-label-sm mb-1">Notas</label>
                <input className="form-control form-control-sm" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="col-12 d-flex justify-content-end gap-2 mt-1">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFormOpen(false)}>Cancelar</button>
                <button type="submit" className="btn btn-sm btn-success" disabled={saving}>
                  {saving ? 'Guardando…' : editId ? 'Guardar cambios' : 'Crear operador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Operators list */}
      {loading && <p className="text-muted text-center py-4">Cargando operadores…</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-muted text-center py-4">Sin operadores registrados. Crea el primero.</p>
      )}

      <div className="d-grid gap-2">
        {filtered.map(op => {
          const isExpanded = expandedId === op.id;
          const exData     = expandData[op.id];
          return (
            <div key={op.id} className="card shadow-sm">
              {/* Operator row */}
              <div
                className="card-body py-2 px-3 d-flex align-items-center gap-3"
                role="button"
                onClick={() => toggleExpand(op.id)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ minWidth: 180 }}>
                  <span className="fw-semibold">{op.name}</span>
                  {op.country && <span className="text-muted ms-2" style={{ fontSize: '0.82em' }}>🌍 {op.country}</span>}
                </div>

                <div className="d-flex gap-1 flex-wrap">
                  <span className={`badge ${STATUS_BADGE[op.status] || 'text-bg-secondary'}`} style={{ fontSize: '0.72em' }}>
                    {STATUS_LABEL[op.status] || op.status}
                  </span>
                  {op.asn && (
                    <span className="badge text-bg-light border text-dark" style={{ fontSize: '0.72em', fontFamily: 'monospace' }}>
                      AS{op.asn}
                    </span>
                  )}
                  <span className="badge text-bg-info text-dark" style={{ fontSize: '0.72em' }}>
                    {op.circuits_count || 0} circuito{op.circuits_count !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="flex-grow-1 text-muted" style={{ fontSize: '0.82em' }}>
                  {op.tech_contact && <span>🔧 {op.tech_contact}</span>}
                  {op.sla && <span className="ms-3">SLA: {op.sla}</span>}
                </div>

                <div className="d-flex gap-1 ms-auto" onClick={e => e.stopPropagation()}>
                  <button className="btn btn-outline-secondary btn-sm py-0 px-2" style={{ fontSize: '0.78em' }}
                    onClick={() => openEdit(op)}>✎</button>
                  <button className="btn btn-outline-danger btn-sm py-0 px-2" style={{ fontSize: '0.78em' }}
                    onClick={() => handleDelete(op)}>✕</button>
                </div>

                <span style={{ fontSize: '0.85em', color: '#6c757d' }}>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {/* Expanded: circuit list */}
              {isExpanded && (
                <div className="border-top bg-light px-3 py-2">
                  {loadingExpand && !exData && (
                    <p className="text-muted py-2 mb-0" style={{ fontSize: '0.85em' }}>Cargando circuitos…</p>
                  )}
                  {exData && (
                    <CircuitMiniList
                      circuits={exData.circuits || []}
                      companyId={companyId}
                      operatorId={op.id}
                      onReload={() => {
                        setExpandData(prev => { const n = { ...prev }; delete n[op.id]; return n; });
                        toggleExpand(op.id);
                      }}
                      navigate={navigate}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Inline circuit mini-list inside an expanded operator row ─────────────── */
const CIRCUIT_TYPE_LABEL = {
  fiber: 'Fibra', xdsl: 'xDSL', ethernet_l2: 'Eth L2', dia: 'DIA',
  mpls: 'MPLS', lte: 'LTE', satellite: 'SAT', other: 'Otro',
};

const CIRCUIT_STATUS_BADGE = {
  active: 'text-bg-success', planned: 'text-bg-warning text-dark',
  maintenance: 'text-bg-warning text-dark', retired: 'text-bg-secondary',
};

const EMPTY_CIRCUIT = { circuit_id_provider: '', label: '', circuit_type: 'fiber',
  bandwidth_down_mbps: '', bandwidth_up_mbps: '', status: 'active', notes: '' };

function CircuitMiniList({ circuits, companyId, operatorId, onReload, navigate }) {
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm]       = useState(EMPTY_CIRCUIT);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createIspCircuit({
        ...form,
        operator_id: operatorId,
        bandwidth_down_mbps: form.bandwidth_down_mbps ? Number(form.bandwidth_down_mbps) : null,
        bandwidth_up_mbps:   form.bandwidth_up_mbps   ? Number(form.bandwidth_up_mbps)   : null,
      }, companyId);
      setAddOpen(false);
      setForm(EMPTY_CIRCUIT);
      onReload();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div>
      {error && <div className="alert alert-danger py-1 px-2 mb-1" style={{ fontSize: '0.82em' }}>{error}</div>}

      {circuits.length === 0 && <p className="text-muted mb-1" style={{ fontSize: '0.85em' }}>Sin circuitos.</p>}

      {circuits.length > 0 && (
        <table className="table table-sm table-borderless mb-1" style={{ fontSize: '0.82em' }}>
          <thead>
            <tr className="text-muted">
              <th>Referencia ISP</th><th>Label</th><th>Tipo</th><th>BW ↓/↑</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {circuits.map(c => (
              <tr key={c.id}>
                <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.circuit_id_provider}</td>
                <td className="text-muted">{c.label || '—'}</td>
                <td><span className="badge text-bg-light border text-dark">{CIRCUIT_TYPE_LABEL[c.circuit_type] || c.circuit_type}</span></td>
                <td>{c.bandwidth_down_mbps ? `${c.bandwidth_down_mbps}/${c.bandwidth_up_mbps || '?'} Mbps` : '—'}</td>
                <td><span className={`badge ${CIRCUIT_STATUS_BADGE[c.status] || 'text-bg-secondary'}`} style={{ fontSize: '0.7em' }}>{c.status}</span></td>
                <td>
                  <button className="btn btn-link btn-sm py-0" style={{ fontSize: '0.78em' }}
                    onClick={() => navigate(`/wan/circuits?operator=${operatorId}&circuit=${c.id}`)}>
                    Ver demarc →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Quick-add circuit */}
      {!addOpen ? (
        <button className="btn btn-outline-primary btn-sm" style={{ fontSize: '0.78em' }}
          onClick={() => setAddOpen(true)}>+ Añadir circuito</button>
      ) : (
        <form onSubmit={handleCreate} className="row g-1 align-items-end mt-1">
          <div className="col-md-3">
            <input className="form-control form-control-sm" required placeholder="Ref. ISP *"
              value={form.circuit_id_provider}
              onChange={e => setForm(f => ({ ...f, circuit_id_provider: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <input className="form-control form-control-sm" placeholder="Label interno"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={form.circuit_type}
              onChange={e => setForm(f => ({ ...f, circuit_type: e.target.value }))}>
              {Object.entries(CIRCUIT_TYPE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="col-md-1">
            <input className="form-control form-control-sm" type="number" placeholder="↓ Mbps"
              value={form.bandwidth_down_mbps}
              onChange={e => setForm(f => ({ ...f, bandwidth_down_mbps: e.target.value }))} />
          </div>
          <div className="col-md-1">
            <input className="form-control form-control-sm" type="number" placeholder="↑ Mbps"
              value={form.bandwidth_up_mbps}
              onChange={e => setForm(f => ({ ...f, bandwidth_up_mbps: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="active">Activo</option>
              <option value="planned">Planificado</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="retired">Retirado</option>
            </select>
          </div>
          <div className="col-md-1 d-flex gap-1">
            <button type="submit" className="btn btn-success btn-sm" disabled={saving}>{saving ? '…' : '✓'}</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAddOpen(false)}>✕</button>
          </div>
        </form>
      )}
    </div>
  );
}
